import { MoreVertical, Pencil, Trash2 } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "src/components/ui/dropdown-menu";
import { Link } from "src/components/ui/link";

interface ConversationItemProps {
  conversation: any;
  isSelected: boolean;
  isStreaming?: boolean;
  hasUnreadResponse?: boolean;
  onDelete: (id: string, e: React.MouseEvent) => void;
  onSelect: (id: string) => void;
  onRename?: (id: string, newTitle: string) => void;
}

const TYPEWRITER_SPEED_MS = 20;

const ConversationItem: React.FC<ConversationItemProps> = ({
  conversation,
  isSelected,
  isStreaming,
  hasUnreadResponse,
  onDelete,
  onSelect,
  onRename,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(conversation.title || "New Conversation");
  const inputRef = useRef<HTMLInputElement>(null);
  const titleRef = useRef<HTMLSpanElement>(null);

  const [menuOpen, setMenuOpen] = useState(false);
  const [isTextTruncated, setIsTextTruncated] = useState(false);

  // Typewriter effect when title changes (e.g. AI-generated title arrives)
  const [displayTitle, setDisplayTitle] = useState(conversation.title || "New Conversation");
  const prevTitleRef = useRef(conversation.title);

  useEffect(() => {
    const newTitle = conversation.title || "New Conversation";
    const prevTitle = prevTitleRef.current;
    prevTitleRef.current = conversation.title;

    // Only animate if the title actually changed and the previous title existed
    // (i.e. this is an update, not the initial render)
    if (prevTitle && newTitle !== prevTitle && newTitle !== "New Conversation" && newTitle !== "New chat") {
      setDisplayTitle("");
      let i = 0;
      const timer = setInterval(() => {
        i++;
        setDisplayTitle(newTitle.slice(0, i));
        if (i >= newTitle.length) {
          clearInterval(timer);
        }
      }, TYPEWRITER_SPEED_MS);
      return () => clearInterval(timer);
    } else {
      setDisplayTitle(newTitle);
      return undefined;
    }
  }, [conversation.title]);

  // Check if text is truncated
  useEffect(() => {
    if (titleRef.current) {
      const isOverflowing = titleRef.current.scrollWidth > titleRef.current.clientWidth;
      setIsTextTruncated(isOverflowing);
    }
  }, [displayTitle]);
  
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedTitle(e.target.value);
  };
  
  const handleTitleBlur = () => {
    if (isEditing) {
      saveTitle();
    }
  };
  
  const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      saveTitle();
    } else if (e.key === "Escape") {
      setIsEditing(false);
    }
  };
  
  const saveTitle = () => {
    const trimmedTitle = editedTitle.trim();
    if (trimmedTitle && trimmedTitle !== conversation.title && onRename) {
      onRename(conversation.id, trimmedTitle);
    }
    setIsEditing(false);
  };
  
  return (
    <div className="relative group">
      <Link
        to={`/chat/c/${conversation.id}`}
        onClick={(e) => {
          // Allow command/ctrl-click to open in new tab
          if (e.metaKey || e.ctrlKey) {
            return;
          }

          // Block navigation while the title is being edited
          if (isEditing) {
            e.preventDefault();
            return;
          }

          // Prevent navigation and delegate handling to parent selector
          e.preventDefault();
          onSelect(conversation.id);
        }}
        className={`py-1 px-3 rounded-md my-1 transition-colors duration-150 flex items-center justify-between ${
          isSelected
            ? "bg-foreground/10 text-foreground border-l-2 border-primary"
            : "text-secondary-foreground hover:bg-foreground/5 hover:text-foreground border-l-2 border-transparent"
        }`}
        variant="noHover"
      >
        <div className="flex flex-col flex-grow overflow-hidden">
          {isEditing ? (
            <input
              ref={inputRef}
              type="text"
              value={editedTitle}
              onChange={handleTitleChange}
              onBlur={handleTitleBlur}
              onKeyDown={handleTitleKeyDown}
              className="bg-secondary border border-accent rounded px-1 py-0.5 text-[13px] text-foreground w-full"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
            />
          ) : (
            <div className="flex-1 overflow-hidden relative">
              <span
                ref={titleRef}
                className={`text-[13px] whitespace-nowrap block ${
                  isTextTruncated ? "group-hover:animate-scroll-text" : ""
                }`}
              >
                {displayTitle}
              </span>
            </div>
          )}
        </div>

        {/* Streaming indicator OR menu button (with optional unread dot) */}
        {isStreaming ? (
          <div className="flex-shrink-0 h-5 w-8 flex items-center justify-end">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
            </span>
          </div>
        ) : (
          <div className="flex-shrink-0 h-5 w-8 flex items-center justify-end relative">
            {hasUnreadResponse && !isSelected && (
              <span className={`absolute right-0 inline-flex rounded-full h-2 w-2 bg-primary pointer-events-none transition-opacity ${menuOpen ? "opacity-0" : "group-hover:opacity-0"}`} />
            )}
            <DropdownMenu
              open={menuOpen}
              onOpenChange={setMenuOpen}
            >
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className={`transition-opacity text-muted-foreground hover:text-foreground flex-shrink-0 h-5 w-8 flex items-center justify-end ${menuOpen ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
                  onClick={(e) => e.stopPropagation()}
                  aria-label="Conversation options"
                  title="Conversation options"
                >
                  <MoreVertical size={16} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-40 bg-secondary border-accent text-foreground"
                // Critical: disable auto focus management!
                onCloseAutoFocus={(e) => {
                  e.preventDefault();
                  if (isEditing) {
                    inputRef.current?.focus();
                  }
                }}
              >
                <DropdownMenuItem
                  className="cursor-pointer hover:bg-card text-foreground focus:bg-card focus:text-foreground"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setMenuOpen(false);
                    setEditedTitle(conversation.title || "New Conversation");
                    setIsEditing(true);
                  }}
                >
                  <Pencil className="mr-2 h-4 w-4" />
                  <span>Rename</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer hover:bg-card text-red-400 focus:bg-card focus:text-red-400"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setMenuOpen(false);
                    onDelete(conversation.id, e);
                  }}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  <span>Delete</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </Link>
    </div>
  );
};

export default ConversationItem;